<!--
  //app/components/pages/region-matches-match-homeTeam-vs-awayTeam/MatchStatsTeamStats.vue
  -->

<script setup lang="ts">
import type { MatchStatusSimplified } from "~/src/match/domain/models/Match";
import type { Event } from "~/src/match/domain/models/Event";
import type { Team } from "~/src/team/domain/models/Team";
import type { MatchStatsTeam } from "~/src/stats/domain/models/MatchStats";
import type { Parameter } from "~/src/stats/domain/models/Parameter";
import type { Player } from "~/src/player/domain/Player";
import matchUiService from "~/src/match/infrastructure/ui-services/matchUiService";
import matchService from "~/src/match/domain/services/matchService";
import matchStatsUiService from "~/src/stats/infrastructure/ui-services/matchStatsUiService";

type Props = {
  parameters: Parameter[];
  homeTeamTotalScore: number | null | undefined;
  awayTeamTotalScore: number | null | undefined;
  matchStatus: MatchStatusSimplified | undefined;
  players: Player[];
  matchEvents: Event[];
  homeTeamInfo: Team;
  awayTeamInfo: Team;
  homeTeamStatsData: MatchStatsTeam;
  awayTeamStatsData: MatchStatsTeam;
  seasonClusterId: number;
};

const props = defineProps<Props>();

const { t } = useI18n();
const { formatEventMinute } = matchUiService();
const { canShowMatchStats, isOwnGoal } = matchService();
const { filterMatchTeamStats } = matchStatsUiService();

const findPlayerById = (playerId: number): Player | undefined => {
  return props.players.find(player => player.id === playerId);
};

const formattedScorers = (events: Event[]) => {
  // event minutes grouped for each player
  const playerMinutesMap = new Map<number, string[]>();

  events.forEach(event => {
    if (!event || !event.playerId) return;

    // Filter out GOL-ALL + CARD-RPE (reverse penalty saved) - no scorer to display
    const parameters = event.parameterAppString?.split(",") || [];
    if (
      parameters.includes("GOL-ALL") &&
      parameters.includes("CARD-RPE") &&
      !parameters.includes("GOL-R-ALL")
    ) {
      return;
    }

    const playerId = event.playerId;
    const eventMinute = formatEventMinute(event, props.seasonClusterId);
    const formattedMinute = event.parameterAppString.split(",").includes("GOL-A")
      ? `${eventMinute} (${t("match.stats.ownGoalAbbreviation")})`
      : eventMinute;

    if (playerMinutesMap.has(playerId)) {
      playerMinutesMap.get(playerId)!.push(formattedMinute);
    } else {
      playerMinutesMap.set(playerId, [formattedMinute]);
    }
  });
  const result: { playerName: string; minutes: string }[] = [];

  playerMinutesMap.forEach((minutes, playerId) => {
    const playerName = findPlayerById(playerId);

    if (playerName) {
      const minuteString = minutes.join(", ");
      result.push({ playerName: playerName.shortName || "-", minutes: minuteString });
    }
  });

  return result;
};

const filterTeamEvents = (events: Event[] | undefined | null, teamId: number): Event[] => {
  if (!events || !events.length || !teamId) return [];

  return events.filter(me => {
    const _isOwnGoal = isOwnGoal(me.parameterAppString);
    return (!_isOwnGoal && me.teamId === teamId) || (me.teamId !== teamId && _isOwnGoal);
  });
};

const homeTeamMatchEvents = filterTeamEvents(props.matchEvents, props.homeTeamInfo.id);
const awayTeamMatchEvents = filterTeamEvents(props.matchEvents, props.awayTeamInfo.id);

const homeTeamGoalEvents = computed((): Event[] => {
  if (!homeTeamMatchEvents) return [];
  return homeTeamMatchEvents.filter(e => e.parameterAppString.split(",").some(p => p === "GOL-ALL"));
});

const awayTeamGoalEvents = computed((): Event[] => {
  if (!awayTeamMatchEvents) return [];
  return awayTeamMatchEvents.filter(e => e.parameterAppString.split(",").some(p => p === "GOL-ALL"));
});

const homeTeamRegularGoals = computed((): Event[] => {
  return homeTeamGoalEvents.value.filter(e => {
    const parameters = new Set(e.parameterAppString.split(","));
    return !["GOL-R-ALL", "GOL-SHO", "GOL-2X"].some(p => parameters.has(p)) && e.half <= 2;
  });
});

const awayTeamRegularGoals = computed((): Event[] => {
  return awayTeamGoalEvents.value.filter(e => {
    const parameters = new Set(e.parameterAppString.split(","));
    return !["GOL-R-ALL", "GOL-SHO", "GOL-2X"].some(p => parameters.has(p)) && e.half <= 2;
  });
});

const homeTeamPenaltyGoals = computed((): Event[] => {
  return homeTeamGoalEvents.value.filter(
    e => e.parameterAppString.split(",").some(p => p === "GOL-R-ALL") && e.half <= 2
  );
});

const homeTeamShootoutGoals = computed((): Event[] => {
  return homeTeamGoalEvents.value.filter(
    e => e.parameterAppString.split(",").some(p => p === "GOL-SHO" || p === "GOL-SHO-PRES") && e.half <= 2
  );
});

const homeTeamDoubleGoals = computed((): Event[] => {
  return homeTeamGoalEvents.value.filter(
    e => e.parameterAppString.split(",").some(p => p === "GOL-2X") && e.half <= 2
  );
});

const awayTeamPenaltyGoals = computed((): Event[] => {
  return awayTeamGoalEvents.value.filter(
    e => e.parameterAppString.split(",").some(p => p === "GOL-R-ALL") && e.half <= 2
  );
});

const awayTeamShootoutGoals = computed((): Event[] => {
  return awayTeamGoalEvents.value.filter(
    e => e.parameterAppString.split(",").some(p => p === "GOL-SHO" || p === "GOL-SHO-PRES") && e.half <= 2
  );
});

const awayTeamDoubleGoals = computed((): Event[] => {
  return awayTeamGoalEvents.value.filter(
    e => e.parameterAppString.split(",").some(p => p === "GOL-2X") && e.half <= 2
  );
});

const combinedStats = computed(() => {
  const homeTeamStats = props.homeTeamStatsData.teamStats || [];
  const awayTeamStats = props.awayTeamStatsData.teamStats || [];

  const homeTeamFilteredStats = filterMatchTeamStats(props.parameters, homeTeamStats);
  const awayTeamFilteredStats = filterMatchTeamStats(props.parameters, awayTeamStats);

  const maxLength = Math.max(homeTeamFilteredStats.length, awayTeamFilteredStats.length);

  const combined = [];

  for (let i = 0; i < maxLength; i++) {
    const homeStat = homeTeamFilteredStats[i] || {};
    const awayStat = awayTeamFilteredStats[i] || {};

    combined.push({
      home: homeStat,
      away: awayStat,
    });
  }

  return combined;
});

const canShowStat = computed((): boolean => {
  return !!props.matchStatus && canShowMatchStats(props.matchStatus);
});
</script>

<template>
  <ul>
    <!--   TOTAL GOALS -->
    <li class="border-b border-gray-700 mb-4">
      <div class="flex justify-between mb-3">
        <p class="text-sm text-primary">
          {{ typeof homeTeamTotalScore === "number" ? homeTeamTotalScore : "-" }}
        </p>
        <p class="text-xs text-gray-200">{{ $t("general.stats.totalGoals") }}</p>
        <p class="text-sm text-primary">
          {{ typeof awayTeamTotalScore === "number" ? awayTeamTotalScore : "-" }}
        </p>
      </div>
    </li>
    <!--   GOALS-->
    <li class="border-b border-gray-700 mb-4">
      <div class="flex justify-between mb-3">
        <div class="flex flex-col w-full items-start mb-3" v-if="homeTeamRegularGoals">
          <p class="text-sm text-primary" v-text="canShowStat ? homeTeamRegularGoals.length : '-'"></p>
          <span
            v-for="(player, index) in formattedScorers(homeTeamRegularGoals)"
            :key="index"
            class="text-gray-400 text-sm mb-1 text-left rtl:text-right"
          >
            {{ player.playerName + " " + player.minutes }}
          </span>
        </div>
        <div>
          <p class="text-xs text-gray-200">{{ $t("general.stats.goals") }}</p>
        </div>
        <div class="flex flex-col w-full items-end mb-3" v-if="awayTeamRegularGoals">
          <p class="text-sm text-primary" v-text="canShowStat ? awayTeamRegularGoals.length : '-'"></p>

          <span
            v-for="(player, index) in formattedScorers(awayTeamRegularGoals)"
            :key="index"
            class="text-gray-400 text-sm mb-1 text-right rtl:text-left"
          >
            {{ player.playerName + " " + player.minutes }}
          </span>
        </div>
      </div>
    </li>
    <!--        COMBINED STATS -->
    <li v-for="stat in combinedStats" :key="stat.id" class="border-b border-gray-700 mb-4">
      <div class="flex justify-between mb-3">
        <!-- Home team stat -->
        <div class="basis-1/3 text-left rtl:text-right">
          <p class="text-sm text-primary me-2">
            <span
              v-if="
                (stat.home.parameterCode === 'PAS-X' || stat.home.parameterCode === 'PSP-X') &&
                typeof stat.home.total === 'number'
              "
            >
              {{ Math.round(Number(stat.home.total)) }}
            </span>
            <span
              v-else-if="stat.home.parameterCode === 'XG' && typeof stat.home.total === 'number'"
              v-text="stat.home.total.toFixed(2)"
            ></span>
            <span
              v-else-if="stat.home.parameterCode === 'GOL-SHO'"
              v-text="canShowStat ? homeTeamShootoutGoals.length : '-'"
            ></span>
            <span
              v-else-if="stat.home.parameterCode === 'GOL-R'"
              v-text="canShowStat ? homeTeamPenaltyGoals.length : '-'"
            ></span>

            <span v-else>{{ stat.home.total }}</span>

            <span v-if="stat.home.parameterMeasureUnit">{{ stat.home.parameterMeasureUnit }}</span>
          </p>

          <!-- Display home team specific goals -->
          <div
            v-if="stat.home.parameterCode === 'GOL-R' && homeTeamPenaltyGoals.length"
            class="flex flex-col w-full items-start mb-3"
          >
            <span
              v-for="(player, index) in formattedScorers(homeTeamPenaltyGoals)"
              :key="index"
              class="text-gray-400 text-sm mb-1 text-left rtl:text-right"
            >
              {{ player.playerName + " " + player.minutes }}
            </span>
          </div>

          <div
            v-if="stat.home.parameterCode === 'GOL-SHO' && homeTeamShootoutGoals.length"
            class="flex flex-col w-full items-start mb-3"
          >
            <span
              v-for="(player, index) in formattedScorers(homeTeamShootoutGoals)"
              :key="index"
              class="text-gray-400 text-sm mb-1"
            >
              {{ player.playerName + " " + player.minutes }}
            </span>
          </div>

          <div
            v-if="stat.home.parameterCode === 'GOL-2X' && homeTeamDoubleGoals.length"
            class="flex flex-col w-full items-start mb-3"
          >
            <span
              v-for="(player, index) in formattedScorers(homeTeamDoubleGoals)"
              :key="index"
              class="text-gray-400 text-sm mb-1 text-left rtl:text-right"
            >
              {{ player.playerName + " " + player.minutes }}
            </span>
          </div>
        </div>

        <!-- Parameter name -->
        <div class="basis-1/3 text-center">
          <p class="text-center text-xs text-gray-200">{{ stat.home.parameterName }}</p>
        </div>

        <!-- Away team stat -->
        <div class="basis-1/3 text-right rtl:text-left">
          <p class="text-sm text-primary ms-2">
            <span
              v-if="
                (stat.home.parameterCode === 'PAS-X' || stat.home.parameterCode === 'PSP-X') &&
                typeof stat.away.total === 'number'
              "
            >
              {{ Math.round(Number(stat.away.total)) }}
            </span>
            <span
              v-else-if="stat.away.parameterCode === 'XG' && typeof stat.away.total === 'number'"
              v-text="stat.away.total.toFixed(2)"
            ></span>
            <span
              v-else-if="stat.away.parameterCode === 'GOL-SHO'"
              v-text="canShowStat ? awayTeamShootoutGoals.length : '-'"
            ></span>
            <span
              v-else-if="stat.away.parameterCode === 'GOL-R'"
              v-text="canShowStat ? awayTeamPenaltyGoals.length : '-'"
            ></span>
            <span v-else>{{ stat.away.total }}</span>

            <span v-if="stat.away.parameterMeasureUnit">{{ stat.away.parameterMeasureUnit }}</span>
          </p>

          <!-- Display away team specific goals -->
          <div
            v-if="stat.away.parameterCode === 'GOL-R' && awayTeamPenaltyGoals.length"
            class="flex flex-col w-full items-end mb-3"
          >
            <span
              v-for="(player, index) in formattedScorers(awayTeamPenaltyGoals)"
              :key="index"
              class="text-gray-400 text-sm mb-1 text-right rtl:text-left"
            >
              {{ player.playerName + " " + player.minutes }}
            </span>
          </div>
          <div
            v-if="stat.away.parameterCode === 'GOL-SHO' && awayTeamShootoutGoals.length"
            class="flex flex-col w-full items-end mb-3"
          >
            <span
              v-for="(player, index) in formattedScorers(awayTeamShootoutGoals)"
              :key="index"
              class="text-gray-400 text-sm mb-1 text-right rtl:text-left"
            >
              {{ player.playerName + " " + player.minutes }}
            </span>
          </div>
          <div
            v-if="stat.away.parameterCode === 'GOL-2X' && awayTeamDoubleGoals.length"
            class="flex flex-col w-full items-end mb-3"
          >
            <span
              v-for="(player, index) in formattedScorers(awayTeamDoubleGoals)"
              :key="index"
              class="text-gray-400 text-sm mb-1 text-right rtl:text-left"
            >
              {{ player.playerName + " " + player.minutes }}
            </span>
          </div>
        </div>
      </div>
    </li>
  </ul>
</template>

<style scoped></style>
